import EditGallery from "@ext/markdown/elements/gallery/edit/components/EditGallery";
import normalizeGalleries from "@ext/markdown/elements/gallery/edit/logic/normalizeGalleries";
import gallerySchema from "@ext/markdown/elements/gallery/edit/model/gallerySchema";
import galleryDropPlugin from "@ext/markdown/elements/gallery/edit/plugins/galleryDropPlugin";
import galleryPastePlugin from "@ext/markdown/elements/gallery/edit/plugins/galleryPastePlugin";
import getExtensionOptions from "@ext/markdown/logic/getExtensionOptions";
import { mergeAttributes, Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";

const CLIPBOARD_EVENTS = new Set(["copy", "cut", "paste"]);
const renderGallery = ReactNodeViewRenderer(EditGallery);

const isTextField = (target: EventTarget | null) =>
	target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

const isItemClipboardEvent = (event: Event) => CLIPBOARD_EVENTS.has(event.type) && !isTextField(event.target);

const Gallery = Node.create({
	...getExtensionOptions({ schema: gallerySchema, name: "gallery" }),

	parseHTML() {
		return [{ tag: "gallery-react-component" }];
	},

	renderHTML({ HTMLAttributes }) {
		return ["gallery-react-component", mergeAttributes(HTMLAttributes), 0];
	},

	addNodeView() {
		return (props) => {
			const nodeView = renderGallery(props);
			const stopEvent = nodeView.stopEvent?.bind(nodeView);
			// The frame sits inside the editable root, so Tiptap takes it for an input and keeps the clipboard
			// events of a selected item away from ProseMirror. The caption is a real input and keeps its own.
			nodeView.stopEvent = (event: Event) => !isItemClipboardEvent(event) && Boolean(stopEvent?.(event));
			return nodeView;
		};
	},

	addProseMirrorPlugins() {
		return [
			galleryDropPlugin(),
			galleryPastePlugin(),
			new Plugin({
				key: new PluginKey("normalizeGalleries"),
				appendTransaction: (transactions, _, state) =>
					transactions.some((tr) => tr.docChanged) ? normalizeGalleries(state) : null,
			}),
		];
	},
});

export default Gallery;
